import * as Constants from '../../../constants';
import {
  ADMIN,
  FULLADMIN,
  LISTADMIN,
  READONLYUSER,
} from '../../../constants/roles';
import useStyles from './CanaryUsers.styles';

import { CanaryManagement, ConfirmModal } from '../../molecules';
import { IUser, IUserDropdown } from '../../../types';
import { TextBlock, UsersTable } from '../../atoms';
import {
  addCanaryUserRequest,
  deleteCanaryUserRequest,
} from '../../../clients/canaryUsers';
import {
  deleteAllCanaryUsersRequest,
  deployToGARequest,
  deployToGAWithInputRequest,
  getCanaryRequest,
  getGAversionRequest,
  goingToGARequest,
  startCanaryRequest,
  terminateRequest,
} from '../../../clients';
import React, { useEffect } from 'react';

import { AddUser } from '../../molecules';
import { ErrorModal } from '../../molecules/ErrorModal';
import { Loader } from '../../atoms/Loader';
import { SingleValue } from 'react-select';
import { UsersFileDelete } from '../UsersFileDelete';
import { UsersFileUpload } from '../UsersFileUpload';
import { compare } from 'compare-versions';
import { findUserRequest } from '../../../clients/users';
import { fullStartCanaryRequest } from '../../../clients/canary';
import { useGlobalContext } from '../../context';

export const CanaryUsers: React.FC = () => {
  const classes = useStyles();
  const { refreshCanary, canary, appConfig, userdata } = useGlobalContext();
  const [isShowModalCanaryStart, setIsShowModalCanaryStart] =
    React.useState<boolean>();
  const [isShowModalCanaryFullStart, setIsShowModalCanaryFullStart] =
    React.useState<boolean>();
  const [isShowModalDeployToGAWithInput, setIsShowModalDeployToGAWithInput] =
    React.useState<boolean>();
  const [isShowModalDeployToGA, setIsShowModalDeployToGA] =
    React.useState<boolean>();
  const [isShowModalTerminate, setIsShowModalTerminate] =
    React.useState<boolean>();
  const [isShowModalConvertToGA, setIsShowModalConvertToGA] =
    React.useState<boolean>();
  const [isShowModalDeleteCanaryUser, setIsShowModalDeleteCanaryUser] =
    React.useState<boolean>();
  const [isShowModalDeleteAllCanaryUsers, setIsShowModalDeleteAllCanaryUsers] =
    React.useState<boolean>();
  const [isLoaded, setIsLoaded] = React.useState<boolean>(false);
  const [isShowContent, setIsShowContent] = React.useState<boolean>(false);
  const [isShowModalFileUpload, setIsShowModalFileUpload] =
    React.useState<boolean>();
  const [isShowModalFileDelete, setIsShowModalFileDelete] =
    React.useState<boolean>();
  const [errorModal, setErrorModal] = React.useState({
    isShowErrorModal: false,
    text: '',
    title: '',
  });
  const [isCanaryVersionLowerThanGA, setIsCanaryVersionLowerThanGA] =
    React.useState<boolean>(false);
  const [isCanaryVersionMatchesGA, setIsCanaryVersionMatchesGA] =
    React.useState<boolean>(false);
  const [version, setVersion] = React.useState<string>();
  const [userToAdd, setUserToAdd] =
    React.useState<SingleValue<IUserDropdown>>();
  const [isAddUserEnabled, setAddUserEnabled] = React.useState<boolean>(false);
  const [users, setUsers] = React.useState<IUser[]>([] as IUser[]);
  const [canaryUserToDelete, setCanaryUserToDelete] =
    React.useState<IUser | null>();

  const setModalErrorText = (text = '', title = '') => {
    if (text && title) {
      setErrorModal({
        isShowErrorModal: true,
        text: text,
        title: title,
      });
    } else {
      setErrorModal({
        isShowErrorModal: false,
        text: text,
        title: title,
      });
    }
  };

  const startCanaryHandler = (canaryVersion: string) => {
    startCanaryRequest(appConfig, canaryVersion)
      .then(response => {
        if (response) {
          refreshCanary(response);
        }
      })
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else if (reason.status && reason.status == 409) {
          reason.json().then((reason_json: { message: string | undefined }) => {
            setModalErrorText(
              reason_json.message,
              Constants.START_CANARY_TITLE,
            );
          });
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      });
    setIsShowModalCanaryStart(false);
  };

  const loadCanaryInfo = () => {
    getCanaryRequest(appConfig)
      .then(response => {
        setIsShowContent(true);
        if (response) {
          if (
            response.status == 'DEPLOYED_TO_GA' ||
            response.status == 'TERMINATED'
          ) {
            response.canaryVersion = undefined;
          }
          refreshCanary(response);
        }
      })
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else if (reason.status && reason.status == 404) {
          getGAversionRequest(appConfig)
            .then(response => {
              setIsShowContent(true);
              if (response) {
                refreshCanary({ gaVersion: response });
              }
            })
            .catch(reason => {
              if (reason.status && reason.status == 403) {
                setModalErrorText(
                  Constants.ACCESS_DENIED_MESSAGE,
                  Constants.ACCESS_DENIED_TITLE,
                );
              } else {
                setModalErrorText(
                  Constants.GENERAL_ERROR_MESSAGE,
                  Constants.GENERAL_ERROR_TITLE,
                );
              }
            });
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      })
      .finally(() => setIsLoaded(true));
  };

  const fullStartCanaryHandler = (uid: string) => {
    fullStartCanaryRequest(appConfig, uid)
      .then(loadCanaryInfo)
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else if (reason.status && reason.status == 409) {
          reason.json().then((reason_json: { message: string | undefined }) => {
            setModalErrorText(
              reason_json.message,
              Constants.FULL_START_CANARY_TITLE,
            );
          });
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      });
    setIsShowModalCanaryFullStart(false);
  };

  const showStartModalHandler = (
    version: string,
    errorInputFormat: boolean,
  ) => {
    if (!errorInputFormat) {
      setVersion(version);
      if (compare(version, canary?.gaVersion.version || '', '<')) {
        setIsCanaryVersionLowerThanGA(true);
        setIsCanaryVersionMatchesGA(false);
        setIsShowModalCanaryStart(true);
      } else if (compare(version, canary?.gaVersion.version || '', '=')) {
        setIsCanaryVersionLowerThanGA(false);
        if (version.includes('*')) {
          setIsCanaryVersionMatchesGA(true);
          setIsShowModalCanaryStart(true);
        } else {
          startCanaryHandler(version);
        }
      } else {
        setIsShowModalCanaryStart(true);
        setIsCanaryVersionLowerThanGA(false);
        setIsCanaryVersionMatchesGA(false);
      }
    }
  };

  const showFullStartModalHandler = () => {
    setIsShowModalCanaryFullStart(true);
  };

  const showDeployToGAModalHandler = () => {
    if (canary?.canaryVersion?.version.includes('*')) {
      setIsShowModalDeployToGAWithInput(true);
    } else {
      setIsShowModalDeployToGA(true);
    }
  };

  const showTerminateModalHandler = () => {
    setIsShowModalTerminate(true);
  };

  const showConvertToGAModalHandler = () => {
    setIsShowModalConvertToGA(true);
  };

  const showDeleteCanaryUserHandler = (userToDelete: IUser) => {
    setCanaryUserToDelete(userToDelete);
    setIsShowModalDeleteCanaryUser(true);
  };

  const showDeleteAllCanaryUsersModalHandler = () => {
    setIsShowModalDeleteAllCanaryUsers(true);
  };

  const closeConfirmModalCanaryStart = () => {
    setIsShowModalCanaryStart(false);
  };

  const closeConfirmModalCanaryFullStart = () => {
    setIsShowModalCanaryFullStart(false);
  };

  const closeConfirmModalDeployToGAWithInput = () => {
    setIsShowModalDeployToGAWithInput(false);
  };

  const closeConfirmModalDeployToGA = () => {
    setIsShowModalDeployToGA(false);
  };

  const closeConfirmModalTerminate = () => {
    setIsShowModalTerminate(false);
  };

  const closeConfirmModalConvertToGA = () => {
    setIsShowModalConvertToGA(false);
  };

  const closeConfirmModalDeleteCanaryUser = () => {
    setCanaryUserToDelete(null);
    setIsShowModalDeleteCanaryUser(false);
  };

  const closeConfirmModalDeleteAllCanaryUsers = () => {
    setIsShowModalDeleteAllCanaryUsers(false);
  };

  const handleErrorModal = () => {
    setModalErrorText();
  };

  const handleOnUsersChange = (users: IUser[]) => {
    setUsers(users);
  };

  const goingToGAHandler = (uid: string) => {
    goingToGARequest(appConfig, uid)
      .then(loadCanaryInfo)
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else if (reason.status && reason.status == 409) {
          reason.json().then((reason_json: { message: string | undefined }) => {
            setModalErrorText(reason_json.message, Constants.GOING_TO_GA_TITLE);
          });
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      });
    setIsShowModalConvertToGA(false);
  };

  const deployToGAHandler = (uid: string) => {
    deployToGARequest(appConfig, uid)
      .then(loadCanaryInfo)
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else if (reason.status && reason.status == 409) {
          reason.json().then((reason_json: { message: string | undefined }) => {
            setModalErrorText(
              reason_json.message,
              Constants.COMPLETE_CANARY_TITLE,
            );
          });
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      });
    setIsShowModalDeployToGAWithInput(false);
    setIsShowModalDeployToGA(false);
  };

  const deployToGAWithInputHandler = (uid: string, canaryVersion: string) => {
    deployToGAWithInputRequest(appConfig, uid, canaryVersion)
      .then(loadCanaryInfo)
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else if (reason.status && reason.status == 409) {
          reason.json().then((reason_json: { message: string | undefined }) => {
            setModalErrorText(
              reason_json.message,
              Constants.COMPLETE_CANARY_TITLE,
            );
          });
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      });
    setIsShowModalDeployToGAWithInput(false);
    setIsShowModalDeployToGA(false);
  };

  const terminateHandler = (uid: string) => {
    terminateRequest(appConfig, uid)
      .then(loadCanaryInfo)
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else if (reason.status && reason.status == 409) {
          reason.json().then((reason_json: { message: string | undefined }) => {
            setModalErrorText(
              reason_json.message,
              Constants.TERMINATE_CANARY_TITLE,
            );
          });
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      });
    setIsShowModalTerminate(false);
  };

  const inputChangeHandler = (userName: string) => {
    if (userName && userName.length >= 2) {
      return findUserRequest(appConfig, userName)
        .then(response => {
          let usersFromAD: IUserDropdown[] = [];
          response.forEach(user => {
            const entry = {
              value: user.userPrincipalName,
              label: user.displayName,
            };
            usersFromAD = [...usersFromAD, entry];
          });
          return usersFromAD;
        })
        .catch(reason => {
          if (reason.status && reason.status == 403) {
            setModalErrorText(
              Constants.ACCESS_DENIED_MESSAGE,
              Constants.ACCESS_DENIED_TITLE,
            );
          } else {
            setModalErrorText(
              Constants.GENERAL_ERROR_MESSAGE,
              Constants.GENERAL_ERROR_TITLE,
            );
          }
          return [];
        });
    }
  };

  const addCanaryUserHandler = () => {
    return addCanaryUserRequest(appConfig, userToAdd?.value)
      .then(response => {
        const createdUser = {
          id: response.userLogin,
          userLogin: response.userLogin,
          displayName: response.displayName,
        };
        setUsers([...users, createdUser]);
        setUserToAdd(null);
        setAddUserEnabled(false);
      })
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else if (reason.status && reason.status == 409) {
          setModalErrorText(
            Constants.USER_IS_ALREADY_IN_CANARY_LIST_MESSAGE,
            Constants.ADD_USER_ERROR_TITLE,
          );
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      });
  };

  const deleteCanaryUserHandler = () => {
    deleteCanaryUserRequest(appConfig, canaryUserToDelete?.userLogin)
      .then(() => {
        const newUsers = users.filter(function (user) {
          return user.userLogin !== canaryUserToDelete?.userLogin;
        });
        setUsers(newUsers);
      })
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else if (reason.status && reason.status == 404) {
          setModalErrorText(
            Constants.USER_TO_DELETE_IS_NOT_FOUND_MESSAGE,
            Constants.DELETE_USER_ERROR_TITLE,
          );
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      });
    setCanaryUserToDelete(null);
    setIsShowModalDeleteCanaryUser(false);
  };

  const addCanaryUsersFromFileHandler = () => {
    setIsShowModalFileUpload(true);
  };

  const deleteCanaryUsersFromFileHandler = () => {
    setIsShowModalFileDelete(true);
  };

  const deleteAllCanaryUsersHandler = () => {
    deleteAllCanaryUsersRequest(appConfig)
      .then(() => {
        setUsers([]);
      })
      .catch(reason => {
        if (reason.status && reason.status == 403) {
          setModalErrorText(
            Constants.ACCESS_DENIED_MESSAGE,
            Constants.ACCESS_DENIED_TITLE,
          );
        } else {
          setModalErrorText(
            Constants.GENERAL_ERROR_MESSAGE,
            Constants.GENERAL_ERROR_TITLE,
          );
        }
      });
    setIsShowModalDeleteAllCanaryUsers(false);
  };

  const onChangeHandler = (
    selectedOption: SingleValue<IUserDropdown>,
    action: { action: string },
  ) => {
    if (action.action === 'clear') {
      setUserToAdd(null);
      setAddUserEnabled(false);
    } else if (action.action === 'select-option') {
      setUserToAdd(selectedOption);
      setAddUserEnabled(true);
    }
  };

  const handleFileUploadSuccessfullyModal = () => {
    setIsShowModalFileUpload(false);
    loadCanaryInfo();
  };

  const handleFileDeleteSuccessfullyModal = () => {
    setIsShowModalFileDelete(false);
    loadCanaryInfo();
  };

  const isAddUserAllowed =
    userdata.roles.includes(ADMIN) ||
    userdata.roles.includes(LISTADMIN) ||
    userdata.roles.includes(FULLADMIN);

  const isAdmin = userdata.roles.includes(ADMIN);
  const isFullAdmin = userdata.roles.includes(FULLADMIN);

  const [isReadOnlyUser, setReadOnlyUser] = React.useState<boolean>(false);

  useEffect(() => {
    setReadOnlyUser(userdata.roles.includes(READONLYUSER));
  }, [userdata]);

  // eslint-disable-next-line
  useEffect(loadCanaryInfo, []);

  return (
    <div>
      {isLoaded && isShowContent && (
        <>
          <div className={classes.root}>
            {(isAdmin || isFullAdmin) && (
              <div className={classes.header}>
                <TextBlock type="pageHeaderTB" text="Treat versions in use" />
              </div>
            )}
            <div className={classes.canaryManagement}>
              <CanaryManagement
                startCanaryHandler={showStartModalHandler}
                fullStartCanaryHandler={showFullStartModalHandler}
                goingToGAHandler={showConvertToGAModalHandler}
                deployToGAHandler={showDeployToGAModalHandler}
                terminateHandler={showTerminateModalHandler}
                canary={canary}
                isCanaryManagementAvailable={isAdmin || isFullAdmin}
              />
            </div>
            <div className={classes.header}>
              <TextBlock type="pageHeaderTB" text="Canary users" />
            </div>
            {isAddUserAllowed && (
              <AddUser
                inputChangeHandler={inputChangeHandler}
                addCanaryUserHandler={addCanaryUserHandler}
                addCanaryUsersFromFileHandler={addCanaryUsersFromFileHandler}
                deleteCanaryUsersFromFileHandler={
                  deleteCanaryUsersFromFileHandler
                }
                deleteAllCanaryUsersHandler={
                  showDeleteAllCanaryUsersModalHandler
                }
                onChangeHandler={onChangeHandler}
                isAddUserEnabled={isAddUserEnabled}
                isAdmin={isAdmin || isFullAdmin}
                isFullAdmin={isFullAdmin}
                value={userToAdd}
              />
            )}
            <UsersTable
              appConfig={appConfig}
              users={users}
              onUsersChangeHandler={handleOnUsersChange}
              onDeleteCanaryUserHandler={showDeleteCanaryUserHandler}
              isUserRemovalAvailable={!isReadOnlyUser}
            />
          </div>
        </>
      )}

      {/* ------------------- */}
      {/* CONFIRM START MODAL */}
      {/* ------------------- */}
      <ConfirmModal
        open={isShowModalCanaryStart}
        confirmModalAction={startCanaryHandler}
        headerText="Start Canary"
        text={
          (isCanaryVersionLowerThanGA
            ? `Please note that the Canary version #${version} is lower than GA #${canary?.gaVersion.version}!\n`
            : '') +
          (isCanaryVersionMatchesGA
            ? `Please note that the Canary version #${version} matches GA #${canary?.gaVersion.version}!\n`
            : '') +
          `Are you sure you want to start the Canary process for Treat #${version}?` +
          (appConfig.enableIntermediateGoingToStatus
            ? ''
            : `After that Canary Treat will be launched for users from the canary list.`)
        }
        buttonText="Apply"
        buttonSize="medium"
        input={version}
        closeConfirmModal={closeConfirmModalCanaryStart}
      />

      {/* ------------------------ */}
      {/* CONFIRM FULL START MODAL */}
      {/* ------------------------ */}
      <ConfirmModal
        open={isShowModalCanaryFullStart}
        confirmModalAction={fullStartCanaryHandler}
        headerText="Full Start Canary"
        text={
          `Are you sure you want to start the Canary process for Treat #${canary?.canaryVersion?.version}? ` +
          `After that Canary Treat will be launched for users from the canary list.`
        }
        buttonText="Apply"
        buttonSize="medium"
        input={canary?.id}
        closeConfirmModal={closeConfirmModalCanaryFullStart}
      />

      {/* ------------------------------- */}
      {/* CONFIRM DELETE ALL CANARY USERS */}
      {/* ------------------------------- */}
      <ConfirmModal
        open={isShowModalDeleteAllCanaryUsers}
        confirmModalAction={deleteAllCanaryUsersHandler}
        headerText="Delete all users"
        text={`Are you sure you want to clear a canary users list?`}
        buttonText="Apply"
        buttonSize="medium"
        input={canary?.id}
        closeConfirmModal={closeConfirmModalDeleteAllCanaryUsers}
      />

      {/* ---------------------------------------- */}
      {/* CONFIRM COMPLETE CANARY MODAL WITH INPUT */}
      {/* ---------------------------------------- */}
      <ConfirmModal
        open={isShowModalDeployToGAWithInput}
        confirmModalAction={deployToGAWithInputHandler}
        headerText="Complete canary"
        text={
          `Are you sure you want to complete the Canary process for Treat #${canary?.canaryVersion?.version}? ` +
          `After that the usage of Treat #${canary?.gaVersion.version} won't be available for all users. ` +
          `Before completion you need specify exact new GA version:`
        }
        buttonText="Apply"
        buttonSize="medium"
        type="withInputField"
        inputMask={canary?.canaryVersion?.version
          .replaceAll('*', '(\\d{1,3})')
          .replaceAll('.', '\\.')}
        inputPlaceholder={canary?.canaryVersion?.version.replaceAll('*', '__')}
        input={canary?.id}
        closeConfirmModal={closeConfirmModalDeployToGAWithInput}
      />

      {/* ----------------------------- */}
      {/* CONFIRM COMPLETE CANARY MODAL */}
      {/* ----------------------------- */}
      <ConfirmModal
        open={isShowModalDeployToGA}
        confirmModalAction={deployToGAHandler}
        headerText="Complete canary"
        text={
          `Are you sure you want to complete the Canary process for Treat #${canary?.canaryVersion?.version}? ` +
          `After that the usage of Treat #${canary?.gaVersion.version} won't be available for all users.`
        }
        buttonText="Apply"
        buttonSize="medium"
        input={canary?.id}
        closeConfirmModal={closeConfirmModalDeployToGA}
      />

      {/* ------------------------------ */}
      {/* CONFIRM TERMINATE CANARY MODAL */}
      {/* ------------------------------ */}
      <ConfirmModal
        open={isShowModalTerminate}
        confirmModalAction={terminateHandler}
        headerText="Terminate canary"
        text={
          `Are you sure you want to terminate Treat #${canary?.canaryVersion?.version}? ` +
          `This version won't be available for all users.`
        }
        buttonText="Apply"
        buttonSize="medium"
        input={canary?.id}
        closeConfirmModal={closeConfirmModalTerminate}
      />

      {/* ---------------------------------- */}
      {/* CONFIRM CONVERT CANARY TO GA MODAL */}
      {/* ---------------------------------- */}
      <ConfirmModal
        open={isShowModalConvertToGA}
        confirmModalAction={goingToGAHandler}
        headerText="Convert Canary to GA"
        text={`Are you sure you want to convert Treat #${canary?.canaryVersion?.version} to GA?`}
        buttonText="Apply"
        buttonSize="medium"
        input={canary?.id}
        closeConfirmModal={closeConfirmModalConvertToGA}
      />

      {/* -------------------------------- */}
      {/* CONFIRM DELETE CANARY USER MODAL */}
      {/* -------------------------------- */}
      <ConfirmModal
        open={isShowModalDeleteCanaryUser}
        confirmModalAction={deleteCanaryUserHandler}
        headerText="Delete a user"
        text={
          `Are you sure you want to delete ${canaryUserToDelete?.userLogin} ` +
          `from canary list?`
        }
        buttonText="Apply"
        buttonSize="medium"
        closeConfirmModal={closeConfirmModalDeleteCanaryUser}
      />

      {/* ----------- */}
      {/* ERROR MODAL */}
      {/* ----------- */}
      <ErrorModal
        open={errorModal.isShowErrorModal}
        headerText={errorModal.title}
        text={errorModal.text}
        buttonText="OK"
        buttonSize="large"
        handleButtonClick={handleErrorModal}
      />

      <UsersFileUpload
        open={isShowModalFileUpload}
        uploadBtnText={'SELECT FILE'}
        closeFileIsUploadedModal={handleFileUploadSuccessfullyModal}
        handleGeneralError={setModalErrorText}
      />

      {(isAdmin || isFullAdmin) && (
        <UsersFileDelete
          open={isShowModalFileDelete}
          uploadBtnText={'SELECT FILE'}
          closeFileIsDeletedModal={handleFileDeleteSuccessfullyModal}
          handleGeneralError={setModalErrorText}
        />
      )}
      {!isLoaded && <Loader />}
    </div>
  );
};
